
import { defineComponent } from 'vue';
import type { PropType } from 'vue';
import {
	IAPIJobPreviewForUser,
	IAPIMatchForJobSeekerListEntry,
	IAPICompanySnippet,
	LikeUser
} from '@hokify/common';
import { lsTest } from '../helpers/localstorage';
import JobListItem from './JobListItem.vue';
import JobListCompanySlot from './JobListCompanySlot.vue';

export interface IJobPreviewItem {
	job: IAPIJobPreviewForUser;
	attributes?: {
		cityCanonical?: string;
		filters?: any[];
		requiredFields?: any[];
	};
}

export default defineComponent({
	name: 'JobList',
	components: {
		JobListItem,
		JobListCompanySlot
	},
	data() {
		const isLoadingSaved: boolean | 'error' = false;
		const preventJobRedirect = false;
		const tagArray = undefined as undefined | string[];
		const tagState: { [key: string]: { occurrence: number; lastIndex: number } } = {};

		return {
			tagState,
			tagArray,
			isLoadingSaved,
			preventJobRedirect,
			relation: undefined,
			satisfactorySlotClosed: false,
			smileys: [
				'icon-smiley-bad',
				'icon-smiley-sad',
				'icon-smiley-indifferent',
				'icon-smiley-smile',
				'icon-smiley-happy'
			]
		};
	},
	computed: {
		enableDebug() {
			return lsTest() && Boolean(localStorage.getItem('debug'));
		}
	},
	created() {
		this.computeTagArray();
		this.isLoadingSaved = true;
	},
	methods: {
		isTagAvailable(tag: string, i: number): boolean {
			let maxOccurence;
			let occurenceInterval;
			switch (tag) {
				case 'newjob':
				case 'popularjob':
					maxOccurence = 3;
					occurenceInterval = 3;
					break;
				case 'nodocument':
				case 'nocv':
					maxOccurence = 2;
					occurenceInterval = 2;
					break;
				case 'nomotivation':
					maxOccurence = 1;
					occurenceInterval = 1;
					break;
				// case 'finalcountdown':
				// case 'nodistancehome':
				// case 'nobirthday':
				// case 'nosalary':
				// 	maxOccurence = 0;
				// 	break;
				default:
					maxOccurence = 0;
					occurenceInterval = Number.POSITIVE_INFINITY;
			}
			return (
				this.tagState[tag]?.occurrence < maxOccurence &&
				Math.abs(this.tagState[tag] && this.tagState[tag].lastIndex - i) > occurenceInterval
			);
		},
		computeTagArray() {
			// reset tagState before each computation
			this.tagState = {};
			const arr: string[] = [];
			if (this.relations) {
				this.relations.forEach((job, index) => {
					if (job.type === 'job') arr.push(this.filterTags(job.obj?.tags, index));
				});
			}

			this.tagArray = arr;
		},
		filterTags(tags: string[], index: number): string {
			const blocklist = ['finalcountdown', 'nopic', 'nobirthday', 'nosalary', 'nodistancehome'];

			// get all tags which are still useable
			const potentialTags: { tag: string; occurrence: number }[] = [];
			tags.forEach(tag => {
				// limit by occurence
				if (!blocklist.includes(tag) && (!this.tagState[tag] || this.isTagAvailable(tag, index))) {
					potentialTags.push({ tag, occurrence: this.tagState[tag]?.occurrence || 0 });
				}
			});

			// sort by usage
			// potentialTags.sort((t1, t2) => t1.occurrence - t2.occurrence);

			// take top 1 and mark as used
			// OLD: const resultTags = potentialTags.slice(0, 1).map(t => t.tag);
			const resultTag = potentialTags[0]?.tag;
			if (!this.tagState[resultTag]) {
				this.tagState[resultTag] = { occurrence: 0, lastIndex: index };
			}
			this.tagState[resultTag].occurrence += 1;
			this.tagState[resultTag].lastIndex = index;

			return resultTag;
		},
		rowItemClicked(job: IAPIJobPreviewForUser, event): void {
			// We need to check whether user is trying to open link in a new tab/window on purpose.
			// Otherwise we make sure that we stay on page and open job in splitview.
			const newTabOrWindow: boolean =
				event.ctrlKey || event.shiftKey || event.metaKey || event.button === 1;
			if (!this.isUserApp && !newTabOrWindow && !this.preventJobRedirect) {
				event?.preventDefault();
				this.$router.push({
					path: `/job/${job.jobNr}`
				});
			}

			if (this.styling !== 'offline') {
				this.$emit('row-item-clicked', job);
			}

			if (this.preventJobRedirect) {
				// always reset to prevent any unwanted behaviour
				this.preventJobRedirect = false;
			}
		},
		getStatus(job: IAPIJobPreviewForUser): boolean {
			const stat =
				this.savedMatches?.some(match => {
					if (match.relation?.obj?._id === job._id) {
						return match.likeuser === LikeUser.Saved;
					}
					return false;
				}) || false;
			return stat;
		},
		debugJob(index: number) {
			return this.jobListDebug?.[index] || {};
		},
		jobsChanged() {
			this.computeTagArray();
		},
		iterateAds(pageNumber: number) {
			if (this.fromRoute === 'startpage') {
				switch (pageNumber) {
					case 3: {
						return 2;
					}
					case 5: {
						return 0;
					}
					default:
						return 1;
				}
			} else if (this.fromRoute === 'gnc') {
				switch (pageNumber) {
					case 3: {
						return 1;
					}
					case 5: {
						return 0;
					}
					default:
						return 2;
				}
			} else {
				switch (pageNumber) {
					case 3: {
						return 1;
					}
					case 5: {
						return 2;
					}
					default:
						return 0;
				}
			}
		},
		clickSatisfactoryLevel(index: number) {
			this.satisfactorySlotClosed = true;
			this.$emit('jobsearch-feedback-clicked', index);
		}
	},
	props: {
		relations: {
			type: Array as PropType<
				| { type: 'company'; obj: IAPICompanySnippet }[]
				| {
						type: 'job';
						obj: IAPIJobPreviewForUser;
						attributes?: {
							cityCanonical?: string;
							filters?: any[];
							requiredFields?: any[];
						};
				  }[]
			>,
			required: false
		},
		jobs: { type: Array as PropType<IJobPreviewItem[]>, required: false },
		jobListDebug: { type: Array },
		activeJob: { type: Number, default: null },
		monochrome: { type: Boolean, default: false },
		minify: { type: Boolean, default: false },
		leanList: { type: Boolean, default: false },
		styling: { type: String, default: 'live' },
		similarJobList: { type: Boolean, default: false },
		currentPage: { type: Number, default: null },
		savedMatches: { type: Array as PropType<IAPIMatchForJobSeekerListEntry[]>, required: false },
		uspCards: {
			type: Array as PropType<{ headline: string; firstLine: string; secondLine: string }[]>,
			required: false
		},
		cvSearchCard: {
			type: Object as PropType<{
				id: string;
				headline: string;
				firstLine: string;
				secondLine: string;
				button: string;
			}>,
			required: false
		},
		region: { type: String, default: 'at' },
		fromRoute: { type: String, required: false },
		satisfactoryTracking: { type: Boolean, default: false },
		isUserApp: { type: Boolean, default: true },
		loggedIn: { type: Boolean, default: false }
	},
	watch: {
		jobs: [
			{
				handler: 'jobsChanged'
			}
		]
	}
});
